import { For, JSXElement, Match, mergeProps, Show, Switch } from 'solid-js';
import { getLocalizedString } from '~/i18n/utils.ts';
import { ActionableContainerWithTitle } from '~/components/Cards';
import RedirectUriItem from '~/pages/FullStackAuthentication/Redirects/RedirectUriItem.tsx';
import { useIntl } from '@cookbook/solid-intl';
import { Button } from '~/components/ui/Button';
import { Link2Icon } from 'lucide-solid';
import { Localized } from '~/i18n';
import {
  getRedirectUriHeader,
  getRedirectUriSubheader,
  RedirectUriFlowTypes,
  RedirectUriType
} from '~/pages/FullStackAuthentication/Redirects/utils.tsx';
import { twMerge } from 'tailwind-merge';
import { getCamelCase, getKebabCase } from '~/utils/commonUtils.ts';
import { getTranslationKey } from '~/pages/Quickstart/utils.ts';

type RedirectUriContainerProps = {
  header?: JSXElement;
  subHeader?: JSXElement;
  isCtaDisabled: boolean;
  redirectUris: string[];
  onCtaClick: () => void;
  onEditClick: (uri: string) => void;
  onDeleteClick?: (uri: string) => void;
  type: RedirectUriType;
  flowType?: RedirectUriFlowTypes;
  containerClass?: string;
};

export default function RedirectUriContainer(
  props: RedirectUriContainerProps
): JSXElement {
  const merged = mergeProps({}, props);
  const intl = useIntl();

  const isQuickStartFlow = () =>
    merged.flowType === RedirectUriFlowTypes.SSO_QUICK_START ||
    merged.flowType === RedirectUriFlowTypes.FSA_QUICK_START;

  const Container = (props: { children: JSXElement }) => (
    <Switch
      fallback={
        <ActionableContainerWithTitle
          id={getCamelCase(merged.type.toLowerCase())}
          header={getRedirectUriHeader(merged.type, intl)}
          subHeader={getRedirectUriSubheader(merged.type, intl)}
          ctaLabel={getLocalizedString('cta_labels.add_redirect_url')}
          onCtaClick={() => merged.onCtaClick()}
          isCtaDisabled={merged.isCtaDisabled}
          class={twMerge(`px-5`, merged.containerClass)}
          ctaDataTestId={getKebabCase(merged.type.toLowerCase()) + '-add-modal'}
        >
          {props.children}
        </ActionableContainerWithTitle>
      }
    >
      <Match when={merged.flowType === RedirectUriFlowTypes.SSO_QUICK_START}>
        <div>
          <Show when={!merged.redirectUris?.length}>
            <div class={`mt-4`}>
              <Button
                size={'sm'}
                variant={`outline`}
                onClick={() => merged.onCtaClick()}
                data-testid={`quickstart-sso-add-callback`}
              >
                <Link2Icon class={`mr-2`} />
                <Localized translationKey={'cta_labels.add_callback_url'} />
              </Button>
            </div>
          </Show>
          {props.children}
        </div>
      </Match>
      <Match when={merged.flowType === RedirectUriFlowTypes.FSA_QUICK_START}>
        <div>
          <Switch>
            <Match when={merged.type === RedirectUriType.POST_LOGIN_URI}>
              <div class={`font-medium mb-2 mt-4`}>
                {getLocalizedString(
                  getTranslationKey('allowed_callback_url_header'),
                  {
                    intl,
                    header: getRedirectUriHeader(merged.type, intl)
                  }
                )}
              </div>
              <p class={`text-fg-muted text-sm`}>
                {getRedirectUriSubheader(merged.type, intl)}
              </p>
            </Match>
            <Match when={merged.type === RedirectUriType.INITIATE_LOGIN_URI}>
              <div class={`font-medium mb-2 mt-4`}>
                {getLocalizedString(
                  getTranslationKey('initiate_login_url_header'),
                  {
                    intl,
                    header: getRedirectUriHeader(merged.type, intl)
                  }
                )}
              </div>
              <p class={`text-fg-muted text-sm`}>
                {getRedirectUriSubheader(merged.type, intl)}
              </p>
            </Match>
          </Switch>
          <Show when={!merged.redirectUris?.length}>
            <div class={`mt-4`}>
              <Button
                size={'sm'}
                variant={`outline`}
                onClick={() => merged.onCtaClick()}
                data-testid={`quickstart-fsa-add-callback`}
              >
                <Link2Icon class={`mr-2`} />
                <Switch>
                  <Match when={merged.type === RedirectUriType.POST_LOGIN_URI}>
                    <Localized translationKey={'cta_labels.add_callback_url'} />
                  </Match>
                  <Match
                    when={merged.type === RedirectUriType.INITIATE_LOGIN_URI}
                  >
                    <Localized
                      translationKey={'cta_labels.add_initiate_login_url'}
                    />
                  </Match>
                </Switch>
              </Button>
            </div>
          </Show>
          {props.children}
        </div>
      </Match>
    </Switch>
  );
  return (
    <>
      <Container>
        <Show when={merged.redirectUris?.length}>
          <div
            class={`w-full`}
            classList={{
              'mt-4': isQuickStartFlow(),
              'mt-6': !isQuickStartFlow()
            }}
          >
            <For each={merged.redirectUris}>
              {(uri) => (
                <RedirectUriItem
                  type={merged.type}
                  flowType={merged.flowType}
                  redirectUri={uri}
                  onEdit={(redirectUri) => merged.onEditClick(redirectUri)}
                  onDelete={(redirectUri) =>
                    merged.onDeleteClick?.(redirectUri)
                  }
                />
              )}
            </For>
          </div>
        </Show>
      </Container>
    </>
  );
}
